import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import NavBar from '../../components/Navbar/NavBar';
import Footer from '../../components/Footer';
import SideMenu from '../../components/SideMenu';
import GalleryComponent from '../../components/Gallery';
import useDocTitle from '../../utils/useDocTitle';
import ContactUs from '../../components/ContactUs';
import { useLanguage } from '../../context/LanguageContext';

const LaboratoryDetails = () => {
  const { programId } = useParams(); // Get the ID from the URL
  const { language } = useLanguage(); // Use LanguageContext for translations
  const [laboratory, setLaboratory] = useState(null);
  const [loading, setLoading] = useState(true);

  useDocTitle(
    laboratory
      ? laboratory.title.rendered
      : language === 'Shqip'
      ? 'Laboratory Details'
      : 'Detajet e Laboratorit'
  );

  useEffect(() => {
    const fetchLaboratory = async () => {
      try {
        const response = await axios.get(
          `https://annexpilot.com/dashboard/wp-json/wp/v2/laboratories/${programId}`
        );
        const data = response.data;

        // Format the data to match the component structure
        setLaboratory({
          id: data.id,
          title: data.title.rendered,
          subtitle: data.subtitle || '',
          description: data.description || '',
          contentDescription: data.content_description || '',
          expert: {
            name: data.expert_name || 'N/A',
            biography:
              data.expert_biography ||
              (language === 'English'
                ? 'Biography not available'
                : 'Biografia nuk është e disponueshme'),
          },
          image: data.image.guid || '',
          gallery:
            data.galleryimage?.map((item) => ({
              src: item.guid,
              description: item.post_content,
              alt: item.post_excerpt,
            })) || [],
        });
      } catch (error) {
        console.error('Error fetching laboratory details:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchLaboratory();
  }, [programId, language]);

  if (loading) {
    return <p>{language === 'English' ? 'Loading...' : 'Duke u ngarkuar...'}</p>;
  }

  if (!laboratory) {
    return <p>{language === 'English' ? 'Laboratory not found.' : 'Laboratori nuk u gjet.'}</p>;
  }

  const menuItems = [
    { href: '#overview', label: language === 'English' ? 'Title' : 'Titulli' },
    { href: '#description', label: language === 'English' ? 'Overview' : 'Përmbledhje' },
    { 
      href: '#expert', 
      label: language === 'English' ? 'Expert' : 'Eksperti',
      subitems: [
        { href: '#expert-biography', label: language === 'English' ? 'Biography' : 'Biografia' },
      ],
    },
    { href: '#gallery', label: language === 'English' ? 'Gallery' : 'Galeria' },
  ];

  return (
    <>
      <NavBar />

      <div className="flex flex-col md:flex-row min-h-screen">
        {/* Sidebar */}
        <aside
        className="lg:w-1/4 bg-gray-100 p-4 lg:p-8 sticky top-16 lg:h-screen"
        aria-label={
            language === 'English'
              ? 'Sidebar navigation for Laboratory details'
              : 'Navigimi anësor për detajet e laboratorit'
          }
        >
          <SideMenu
            backLink="/educational-program/laboratories"
            backText={language === 'English' ? 'LABORATORIES' : 'LABORATORËT'}
            menuItems={menuItems}
          />
        </aside>

        {/* Main Content */}
        <div className="w-full lg:w-3/4 p-4 lg:p-8">
          {/* Overview Section */}
          <div className="container-inner mb-16 md:mb-12">
            {laboratory.image && (
              <img
                src={laboratory.image}
                alt={laboratory.title}
                className="w-full h-auto rounded shadow-md mb-6 md:mb-8"
                aria-describedby="overview-title"
              />
            )}
            <h1
              id="overview"
              className="text-3xl md:text-5xl font-bold mb-4 md:mb-8"
              tabIndex="0"
            >
              {laboratory.title}
            </h1>
            {laboratory.subtitle && (
              <h2 className="text-2xl md:text-3xl font-semibold mb-4 md:mb-8">
                {laboratory.subtitle}
              </h2>
            )}
          </div>

          {/* Description Section */}
          <div id="description" className="container-inner mb-8 md:mb-12">
            <h2 className="text-2xl font-semibold mb-4" tabIndex="0">
              {language === 'English' ? 'Overview' : 'Përmbledhje'}
            </h2>
            <div
              className="text-base md:text-lg text-gray-700 leading-relaxed"
              dangerouslySetInnerHTML={{ __html: laboratory.contentDescription }}
              aria-label={
                language === 'English'
                  ? 'Description of the laboratory'
                  : 'Përshkrimi i laboratorit'
              }
            ></div>
          </div>

          {/* Expert Section */}
          {laboratory.expert && (
            <div id="expert" className="container-inner mb-8 md:mb-12">
              <h2 className="text-2xl font-semibold mb-4" tabIndex="0">
                {language === 'English' ? 'About the Expert' : 'Për Ekspertin'}
              </h2>
              <div id="expert-biography">
                <h3 className="text-xl font-semibold mb-4" tabIndex="0">
                  {laboratory.expert.name}
                </h3>
                <div
                  className="text-base md:text-lg text-gray-700 leading-relaxed"
                  dangerouslySetInnerHTML={{
                    __html: laboratory.expert.biography,
                  }}
                  aria-label={
                    language === 'English'
                      ? 'Biography of the expert'
                      : 'Biografia e ekspertit'
                  }
                ></div>
              </div>
            </div>
          )}

          {/* Gallery Section */}
          {laboratory.gallery && laboratory.gallery.length > 0 && (
            <section id="gallery" className="container-inner mb-8 md:mb-12">
              <h2 className="text-2xl md:text-3xl font-semibold mb-4" tabIndex="0">
                {language === 'English' ? 'Gallery' : 'Galeria'}
              </h2>
              <GalleryComponent images={laboratory.gallery} />
            </section>
          )}

          <ContactUs />
        </div>
      </div>

      <Footer />
    </>
  );
};

export default LaboratoryDetails;